import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Menu, // Added Menu import
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector
} from "@mui/x-data-grid";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import moment from "moment";
import InventoryServices from "../../services/InventoryServices";
import { useDispatch } from "react-redux";
import { setShowMessage } from "../../redux/slices/notificationSlice";
import TimePeriods from "../table/panel/TimePeriods";
import translations from "../../assets/locals/translations";
import PersistentDataGrid from "../table/PersistentDataGrid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons/faEllipsisV";

const DeliveriesData = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentLanguage = useSelector((state) => state.language.language);
  const t = translations[currentLanguage] || translations['en'];
  const InventoryService = new InventoryServices();
  const [stockData, setStockData] = useState([]);
  const theme = useTheme();
  // Adjust grid container based on screen size for responsiveness
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Global search state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedType, setSelectedType] = useState("Today");
  const [isDataLoading, setIsDataLoading] = useState(false);

  // Helper: get current financial year string like "2024-2025"
  const getCurrentFinancialYearString = () => {
    const today = new Date();
    const currentMonth = today.getMonth(); // 0 (Jan) - 11 (Dec)
    let currentYear = today.getFullYear();
    if (currentMonth < 3) {
      // Before April (Jan, Feb, Mar)
      return `${currentYear - 1}-${currentYear}`;
    } else {
      // April onwards
      return `${currentYear}-${currentYear + 1}`;
    }
  };

  const [selectedYear, setSelectedYear] = useState(
    getCurrentFinancialYearString()
  );

  // column visibility model state (needed for hide/show to immediately update)
  const [deliveryColumnShowHideModel, setDeliveryColumnShowHideModel] = useState({});
  const handleDeliveryColumnShowHideChange = (newModel) => {
    setDeliveryColumnShowHideModel(newModel);
    localStorage.setItem('deliveries_columnShowHide', JSON.stringify(newModel));
  };

  // density state (to persist Compact/Standard/Comfortable choice)
  const [densityValue, setDensityValue] = useState(() => {
    const saved = localStorage.getItem('deliveries_density');
    return saved || 'standard';
  });

  const gridApiRef = useRef(null);

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem('deliveries_columnShowHide'));
    if (saved) {
      setDeliveryColumnShowHideModel(saved);
    }
  }, []);

  // Monitor and persist density changes
  useEffect(() => {
    if (!gridApiRef.current) return;

    const interval = setInterval(() => {
      const factor = gridApiRef.current?.state?.preferencePanel?.densityFactor;
      let current = 'standard';
      if (factor === 0.5) current = 'compact';
      else if (factor === 1.5) current = 'comfortable';

      if (current !== densityValue) {
        setDensityValue(current);
        localStorage.setItem('deliveries_density', current);
      }
    }, 300);

    return () => clearInterval(interval);
  }, [densityValue]);

  const yearOptions = useMemo(() => {
    const options = [];
    const firstPossibleStartYear = 2012;
    const currentFYString = getCurrentFinancialYearString();
    const currentFYStartYear = parseInt(currentFYString.split("-")[0], 10);

    for (let y = currentFYStartYear; y >= firstPossibleStartYear; y--) {
      const financialYearLabel = `${y}-${y + 1}`;
      options.push({ value: financialYearLabel, label: financialYearLabel });
    }
    options.push({ value: "1998-2012", label: "1998-2012" });
    return options;
  }, []);

  const [loading, setLoading] = useState(false);

  // Persist pagination state (page and pageSize) in sessionStorage
  const pageSizeKey = `rowsPerPage_${window.location.pathname}`;
  const pageKey = `currentPage_${window.location.pathname}`;

  const [page, setPage] = useState(() => {
    const saved = sessionStorage.getItem(pageKey);
    return saved ? parseInt(saved, 10) : 0;
  });

  const [pageSize, setPageSize] = useState(() => {
    const saved = sessionStorage.getItem(pageSizeKey);
    return saved ? parseInt(saved, 10) : 50;
  });
  const [totalCount, setTotalCount] = useState(0);
  const [excelResult, setExcelResult] = useState([]);

  const fileInputRef = useRef(null);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deliveryToDelete, setDeliveryToDelete] = useState(null);

  const handleSampleDownload = async () => {
    try {
      const response = await InventoryService.downloadSampleDelivery();
      if (response && response.data) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Delivery_Sample.xlsx");
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
      }
    } catch (error) {
      console.error("Error downloading sample:", error);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setLoading(true);
    try {
      const response = await InventoryService.uploadDeliveryExcel(formData);
      if (response && response.isSuccess) {
        // Auto-download failed rows Excel (if any)
        const failedFile = response.failedFile;
        const failedCount = response.failed;

        if (failedCount > 0 && failedFile) {
          try {
            const baseUrl = process.env.REACT_APP_NODE_URL || "";
            const downloadUrl = failedFile.startsWith("http")
              ? failedFile
              : `${baseUrl.replace(/\/$/, "")}${failedFile.startsWith("/") ? "" : "/"}${failedFile}`;

            const fileName =
              (failedFile.split("/").pop() || "failed_delivery.xlsx").split("?")[0] ||
              "failed_delivery.xlsx";

            // Fetch as blob to make auto-download reliable (anchors + cross-origin often fail).
            const resp = await fetch(downloadUrl, { method: "GET" });
            if (!resp.ok) {
              throw new Error(`Failed download: ${resp.status} ${resp.statusText}`);
            }

            const blob = await resp.blob();
            const objectUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = objectUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(objectUrl);
          } catch (e) {
            console.error("Auto-download failed file error:", e);
          }
        }

        dispatch(
          setShowMessage(
            `Upload completed. Success: ${response.success ?? 0}, Failed: ${
              response.failed ?? 0
            }`,
          ),
        );
        getDeliveryLogs(
          page,
          pageSize,
          selectedDates,
          selectedType,
          searchQuery
        );
      } else {
        dispatch(setShowMessage(response?.message || "Upload failed"));
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      dispatch(setShowMessage("Error uploading file"));
    } finally {
      setLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const getAllDates = (dates) => {
    if (!dates || dates.length === 0) return [];
    const [startDate, endDate] = dates.map((date) => new Date(date));
    const dateArr = [];
    const formatDate = (date) => {
      let year = date.getFullYear();
      let month = (date.getMonth() + 1).toString().padStart(2, "0");
      let day = date.getDate().toString().padStart(2, "0");
      return `${year}-${month}-${day}`;
    };
    let currentDate = new Date(startDate);
    if (endDate === undefined) {
      dateArr.push(formatDate(currentDate));
      return dateArr;
    } else {
      while (currentDate <= endDate) {
        dateArr.push(formatDate(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }
      return dateArr;
    }
  };

  const getDeliveryLogs = async (
    page = 0,
    pageSize = 50,
    dates,
    type,
    search = searchQuery
  ) => {
    const customDates = getAllDates(dates);
    let startDate = "";
    let endDate = "";

    if (dates && dates.length > 0) {
      startDate = dates[0];
      endDate = dates[dates.length - 1];
    }

    if (type === "All") {
      startDate = "";
      endDate = "";
    } else if (type === "Custom") {
      startDate = customDates[0];
      endDate = customDates[customDates.length - 1];
    }

    setLoading(true);
    try {
      const payload = {
        ev_stage: "DELIVERY",
        category: 2,
        page: page + 1, // Convert 0-index to 1-index for API
        limit: pageSize,
        searchQuery: search,
        ...(search.trim() === "" && { startDate, endDate }),
        financialYear: selectedYear,
      };

      if (!payload && Object.keys(payload).length === 0) {
        return;
      }

      const response = await InventoryService.getDeliveryLogs(payload);
      if (response && response.isSuccess) {
        const result = response.result;
        setStockData(result);
        setTotalCount(response.totalCount);
        setExcelResult(response.excelResult);
      }
    } catch (error) {
      console.error(error, "Error getting stocks list");
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when page or pageSize changes
  useEffect(() => {
    getDeliveryLogs(page, pageSize, selectedDates, selectedType, searchQuery);
  }, [page, pageSize]);

  // When selectedYear changes
  useEffect(() => {
    if (!selectedYear) return;
    if (selectedYear === "1998-2012") {
      setSelectedType("All");
      setSelectedDates([]);
      setPage(0);
      getDeliveryLogs(0, pageSize, [], "All", searchQuery);
      return;
    }

    const parts = selectedYear.split("-").map((p) => parseInt(p, 10));
    if (parts.length !== 2 || Number.isNaN(parts[0]) || Number.isNaN(parts[1])) return;
    const startYear = parts[0];
    const startDate = `${startYear}-04-01`;
    const endDate = `${startYear + 1}-03-31`;
    setSelectedType("Custom");
    setSelectedDates([startDate, endDate]);

    setPage(0);
    getDeliveryLogs(0, pageSize, [startDate, endDate], "Custom", searchQuery);
  }, [selectedYear]);

  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
  };

  function editDeliveryData(data) {
    navigate(`/delivery/data/edit`, {
      state: { data: data },
    });
  }

  const deleteActionCall = (row) => {
    setDeliveryToDelete(row);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const row = deliveryToDelete;
      const deliveryId = row?.deliveryId || row?.id || row?.delivery_id;
      if (!deliveryId) {
        dispatch(setShowMessage("Unable to identify delivery ID"));
        setDeleteDialogOpen(false);
        return;
      }

      const resp = await InventoryService.deleteDelivery(deliveryId);
      if (resp?.isSuccess || resp?.success) {
        dispatch(setShowMessage(resp?.message || "Delivery deleted"));
        getDeliveryLogs(page, pageSize, selectedDates, selectedType, searchQuery);
        setDeleteDialogOpen(false);
        return;
      }

      dispatch(setShowMessage(resp?.message || "Delete failed"));
    } catch (e) {
      console.error(e);
      dispatch(setShowMessage("Server error while deleting delivery."));
    } finally {
      setDeleteDialogOpen(false);
      setDeliveryToDelete(null);
    }
  };


  // Fetch data when searchQuery changes (with debounce)
  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(0);
      getDeliveryLogs(0, pageSize, selectedDates, selectedType, searchQuery);
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  const columns = [
    { field: "bil_no", headerName: t.bilNo || "BIL NO", flex: 1, minWidth: 150 },
    {
      field: "date",
      headerName: t.date || "DATE",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => {
        if (!params.value) return "";
        const date = moment(params.value);
        return date.isValid() ? date.format("DD-MM-YYYY") : "";
      },
    },
    { field: "ndp", headerName: t.ndp || "NDP", flex: 1, minWidth: 120 },
    { field: "bum_cod", headerName: t.bumCod || "BUM COD", flex: 1, minWidth: 100 },
    { field: "model_name", headerName: t.modal || "MODAL", flex: 1, minWidth: 150 },
    { field: "chassis", headerName: t.chassis || "CHASSIS", flex: 1, minWidth: 200 },
    { field: "engine", headerName: t.engine || "ENGINE", flex: 1, minWidth: 200 },
    {
      field: "delivery_date",
      headerName: t.deliveryDate || "Delivery Date",
      flex: 1,
      minWidth: 180,
      valueFormatter: (params) => {
        if (!params.value) return "";
        const date = moment(params.value);
        return date.isValid() ? date.format("DD-MM-YYYY") : "";
      },
    },
    {
      field: "phone_number",
      headerName: t.phoneNumber || "Phone Number",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "customer_name",
      headerName: t.customerName || "Customer Name",
      flex: 1,
      minWidth: 310,
    },
    {
      field: "village",
      headerName: t.village || "Village",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "taluka",
      headerName: t.taluka || "Taluka",
      flex: 1,
      minWidth: 200,
    },

    {
      field: "menu",
      headerName: <FontAwesomeIcon icon={faEllipsisV} style={{ marginRight: "15px" }} />,
      className: "bg-dark",
      sortable: false,
      filterable: false,
      headerAlign: "right",
      align: "right",
      disableColumnMenu: true,
      width: 130,
      position: "sticky",
      renderCell: (params) => (
        <div className="d-flex justify-content-center dotHoverempicon ">
          <FontAwesomeIcon icon={faEllipsisV} />
          <div className="expandDiv">
            <Tooltip title={translations[currentLanguage].edit}>
              <button className="myActionBtn m-1" onClick={() => editDeliveryData(params.row)}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" className="bi bi-pencil-square" viewBox="0 0 16 16">
                  <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
                  <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z" />
                </svg>
              </button>
            </Tooltip>
            <Tooltip title={translations[currentLanguage].delete || "Delete"}>
              <button onClick={() => deleteActionCall(params.row)} className="myActionBtn m-1">
                <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" className="bi bi-trash3" viewBox="0 0 16 16">
                  <path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5ZM11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H2.506a.58.58 0 0 0-.01 0H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1h-.995a.59.59 0 0 0-.01 0H11Zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5h9.916Zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47ZM8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5Z" />
                </svg>
              </button>
            </Tooltip>
          </div>
        </div>
      ),
    },
  ];

  const excelColumns = [
    { field: "bil", headerName: "BIL" },
    { field: "bil_no", headerName: "BIL NO" },
    { field: "date", headerName: "DATE" },
    { field: "bum_cod", headerName: "BUM COD" },
    { field: "model_name", headerName: "MODAL" },
    { field: "chassis", headerName: "CHASSIS" },
    { field: "engine", headerName: "ENGINE" },
    { field: "delivery_date", headerName: "DELIVERY DATE" },
    { field: "phone_number", headerName: "PHONE NUMBER" },
    { field: "customer_name", headerName: "CUSTOMER NAME" },
    { field: "village", headerName: "VILLAGE" },
    { field: "taluka", headerName: "TALUKA" },
    { field: "district", headerName: "DISTRICT" },
    { field: "sales_person_name", headerName: "Sales Person" },
    { field: "delivery_salesman", headerName: "Delivery Salesman" },
    { field: "created_by", headerName: "Created By" },
    { field: "enquiry_date", headerName: "Enquiry Date" },
    { field: "whatsapp_number", headerName: "Whatsapp Number" },
    { field: "delivery_manufacture", headerName: "Manufacture" },
    { field: "old_details", headerName: "Old Detail" },
    { field: "followup_details", headerName: "Followup Details" },
    { field: "payment_mode", headerName: "Payment Mode" },
    { field: "payment_details", headerName: "Payment Details" },
    { field: "new_tractor_price", headerName: "New Price" },
    { field: "old_tractor_price", headerName: "Old Price" },
    { field: "remaining_price", headerName: "Remaining Price" },
    { field: "retail_date", headerName: "Retail Date" },
    { field: "primary_source", headerName: "Primary Source" },
    { field: "enquiry_source", headerName: "Enquiry Source" },
    { field: "chassis_print", headerName: "CHASSIS PRINT" },
    { field: "parsing_type", headerName: "PARSING TYPE" },
    { field: "subsidy", headerName: "SUBSIDY" },
    { field: "aadhar_card", headerName: "AADHAR CARD" },
    { field: "jamin_no", headerName: "JAMIN NO" },
    { field: "bank_passbook", headerName: "BANK PASSBOOK" },
    { field: "retail_bill_no", headerName: "RETAIL BILL NO" },
    { field: "insurance", headerName: "INSURANCE" },
    { field: "tax", headerName: "TAX" },
    { field: "penalty", headerName: "PENALTY" },
    { field: "total", headerName: "TOTAL" },
    { field: "registration_no", headerName: "REGISTRATION NO" },
    { field: "no_plate_ordered", headerName: "PLATE NUMBER ORDER" },
    { field: "rc_fitment", headerName: "RC FITMENT" },
    { field: "remark", headerName: "REMARK" },
  ];

  // ================= EXPORT LOGIC =================
  const generateCSV = (columns, data) => {
    const headers = columns.map((col) => col.headerName);
    const rows = data.map((row) =>
      columns.map((col) => {
        let raw = row[col.field];
        // Date formatting for CSV
        if (
          raw != null &&
          (col.field === "date" || col.field === "delivery_date" || col.field === "enquiry_date" || col.field === "retail_date")
        ) {
          const m = moment(raw);
          raw = m.isValid() ? m.format("DD-MM-YYYY") : "";
        }

        // String escaping
        if (raw != null) {
          return String(raw).replace(/"/g, '""');
        }
        return "";
      })
    );

    const csvLines = [
      headers.join(","),
      ...rows.map((cols) => `"${cols.join('","')}"`),
    ];
    return csvLines.join("\n");
  };

  const handleExportAll = async () => {
    try {
      setIsDataLoading(true);

      // Date Logic
      const customDates = getAllDates(selectedDates);
      let startDateStr = "";
      let endDateStr = "";

      if (selectedDates && selectedDates.length > 0) {
        startDateStr = selectedDates[0];
        endDateStr = selectedDates[selectedDates.length - 1];
      }

      if (selectedType === "All") {
        startDateStr = "";
        endDateStr = "";
      } else if (selectedType === "Custom") {
        startDateStr = customDates[0];
        endDateStr = customDates[customDates.length - 1];
      }

      // API Payload: Request ALL records (limit = totalCount)
      const payload = {
        ev_stage: "DELIVERY",
        category: 2,
        page: 1, // Doesn't matter much if fetching all, but 1 is safe
        limit: totalCount > 0 ? totalCount : 5000,
        searchQuery: searchQuery,
        ...(searchQuery.trim() === "" && { startDate: startDateStr, endDate: endDateStr }),
        financialYear: selectedYear,
      };

      const response = await InventoryService.getDeliveryLogs(payload);

      if (response && response.isSuccess) {
        // Determine which result set to use. 
        // If API sends back 'excelResult' containing everything, use that. 
        // Otherwise use 'result' which now contains all items due to limit setting.
        const dataToExport = response.excelResult && response.excelResult.length > 0
          ? response.excelResult
          : response.result;

        const csvContent = generateCSV(excelColumns, dataToExport);

        const today = new Date();
        const fileName = `DELIVERIES_ALL_${selectedType}_${moment(today).format("YYYY-MM-DD")}.csv`;

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

      } else {
        alert("Failed to fetch data for export");
      }

    } catch (error) {
      console.error("Export Error:", error);
      alert("An error occurred during export.");
    } finally {
      setIsDataLoading(false);
    }
  };

  // --- CUSTOM TOOLBAR ---
  const CustomToolbar = () => {
    // Dropdown Menu State
    const [anchorEl, setAnchorEl] = useState(null);
    const openMenu = Boolean(anchorEl);

    // Open Menu
    const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
    };

    // Close Menu
    const handleClose = () => {
      setAnchorEl(null);
    };

    // Option 1: CSV Download
    const handleCsvClick = () => {
      handleExportAll();
      handleClose();
    };

    // Option 2: Print
    const handlePrintClick = () => {
      window.print();
      handleClose();
    };

    return (
      <GridToolbarContainer sx={{ justifyContent: 'flex-end' }}>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />

        {/* Export Button -> Opens Menu */}
        <Button
          id="export-button"
          aria-controls={openMenu ? 'export-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={openMenu ? 'true' : undefined}
          size="small"
          startIcon={<FileDownloadIcon />}
          onClick={handleClick}
          disabled={isDataLoading || totalCount === 0}
          sx={{
            color: 'primary.main',
            textTransform: 'uppercase',
            fontSize: '0.8125rem',
            padding: '4px 5px'
          }}
        >
          {isDataLoading ? "EXPORTING..." : "EXPORT"}
        </Button>

        {/* Dropdown Menu */}
        <Menu
          id="export-menu"
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'export-button',
          }}
        >
          <MenuItem onClick={handleCsvClick}>Download as CSV</MenuItem>
          <MenuItem onClick={handlePrintClick}>Print</MenuItem>
        </Menu>
      </GridToolbarContainer>
    );
  };
  // ============================================

  const handleApplyButton = async (dates, type) => {
    setSelectedType(type);
    setSelectedDates(dates);
    setPage(0);
    await getDeliveryLogs(0, pageSize, dates, type, searchQuery);
  };

  return (
    <div className="bg-white p-2">
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <TextField
          variant="outlined"
          placeholder={t.globalSearch || "Global Search..."}
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: isMobile ? "100%" : 300 }}
        />
        <div className="d-flex justify-content-end ms-1">
          <FormControl
            size="small"
            sx={{ minWidth: 160, mr: 1, mb: isMobile ? 1 : 0 }}
          >
            <InputLabel id="year-select-label">{t.financialYear || "Financial Year"}</InputLabel>
            <Select
              labelId="year-select-label"
              id="year-select"
              value={selectedYear}
              label={t.financialYear || "Financial Year"}
              onChange={handleYearChange}
            >
              {yearOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TimePeriods handleApplyButton={handleApplyButton} />

          <button
            className="btn btn-outline-info d-flex align-items-center ms-1"
            onClick={handleSampleDownload}
            title="Download Sample Format"
          >
            {t.sample || "Sample"}
          </button>

          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            accept=".xlsx, .xls"
            onChange={handleFileUpload}
          />
          <button
            className="btn btn-outline-success d-flex align-items-center ms-1"
            onClick={() => fileInputRef.current.click()}
            title="Upload Excel File"
          >
            {t.upload || "Upload"}
          </button>

          {/* External Export Button removed in favor of Grid Toolbar */}
        </div>
      </Box>

      <div
        className="tableMenuHover"
        style={{ flexGrow: 1, width: "100%", height: "calc(100vh - 70px)" }}
      >
        <PersistentDataGrid
          rows={stockData}
          columns={[
            {
              field: "deliveryId",
              headerName: t.bil || "BIL",
              width: 80,
              valueGetter: (params) => {
                const index = params.api.getAllRowIds().indexOf(params.row.deliveryId);
                return (page * pageSize) + index + 1;
              },
            },
            ...columns,
          ]}
          columnVisibilityModel={deliveryColumnShowHideModel}
          onColumnVisibilityModelChange={handleDeliveryColumnShowHideChange}
          apiRef={gridApiRef}
          initialState={{
            preferencePanel: {
              densityValue: densityValue,
            },
          }}
          getRowId={(row) => row.deliveryId}
          pagination
          paginationMode="server"
          rowCount={totalCount}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(newModel) => {
            setPage(newModel.page);
            setPageSize(newModel.pageSize);
            try {
              sessionStorage.setItem(pageKey, String(newModel.page));
              sessionStorage.setItem(pageSizeKey, String(newModel.pageSize));
            } catch (e) { }
          }}
          pageSizeOptions={[10, 15, 25, 50]}
          disableSelectionOnClick
          autoHeight={isMobile}

          // --- Custom Toolbar ---
          components={{
            Toolbar: CustomToolbar,
            NoRowsOverlay: () => (
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                }}
              >
                <span style={{ fontFamily: "Poppins" }}>{t.noDataAvailable || "No Data Available"}</span>
              </div>
            ),
          }}
          // ----------------------

          componentsProps={{
            toolbar: {
              position: "right",
              style: { fontFamily: "Poppins", alignSelf: "end" },
            },
          }}
          sx={{
            width: "100%",
            "& .MuiDataGrid-columnHeaderTitle": {
              textTransform: "uppercase",
            },
          }}
          loading={loading || isDataLoading}
        />
      </div>
      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-delivery-dialog-title"
      >
        <DialogTitle id="delete-delivery-dialog-title">
          {translations[currentLanguage].deleteDeliveryTitle || "Delete Delivery"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {translations[currentLanguage].deleteDeliveryText ||
              "Are you sure you want to delete this delivery? This action cannot be undone."}
          </DialogContentText>
          <Box mt={2} p={1} bgcolor="#f7f7f9" borderRadius={1}>
            <div>
              <strong>Bill No:</strong> {deliveryToDelete?.bil_no ?? "-"}
            </div>
            <div>
              <strong>Chassis:</strong> {deliveryToDelete?.chassis ?? "-"}
            </div>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
            {translations[currentLanguage].cancel || "Cancel"}
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            {translations[currentLanguage].delete || "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DeliveriesData;